import { Session } from '../features/sessions/models/Session'

/**
 * Calculates the end time of a session based on its start date, time, and duration
 * @param session - The session object containing dateStart, timeLocal, and duration
 * @returns The calculated end time as a Date object
 */
export const calculateSessionEndDateTime = (session: Session): Date => {
  // Create a proper date object combining the date and time
  const eventDate = new Date(session.dateStart);
  const [hours, minutes] = session.timeLocal.split(':').map(Number);
  
  // Set the time on the event date
  const sessionStartDateTime = new Date(eventDate);
  sessionStartDateTime.setHours(hours, minutes, 0, 0);
  
  // Add duration in minutes to get end time
  const sessionEndTime = new Date(sessionStartDateTime.getTime() + (session.duration * 60000));
  
  return sessionEndTime;
};

/**
 * Determines if a session is scheduled for the future (has not ended yet)
 * @param session - The session object to check
 * @returns true if the session is upcoming, false if it has already ended
 */
export const isSessionScheduledForFuture = (session: Session): boolean => {
  const sessionEndTime = calculateSessionEndDateTime(session);
  return sessionEndTime >= new Date();
};

/**
 * Sorts sessions based on whether they are upcoming or completed
 * @param sessions - Array of sessions to sort
 * @param isUpcomingSessions - true for upcoming sessions (earliest first), false for completed sessions (latest first)
 * @returns Sorted array of sessions
 */
export const sortSessionsByDate = (sessions: Session[], isUpcomingSessions: boolean): Session[] => {
  return [...sessions].sort((a, b) => {
    const dateA = new Date(a.dateStart);
    const dateB = new Date(b.dateStart);
    
    if (isUpcomingSessions) {
      // For upcoming sessions, show earliest first
      return dateA.getTime() - dateB.getTime();
    } else {
      // For past sessions, show latest first
      return dateB.getTime() - dateA.getTime();
    }
  });
};
